import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/site/Header";
import Hero from "@/components/site/Hero";
import Services from "@/components/site/Services";
import ServiceBreakout from "@/components/site/ServiceBreakout";
import Consulting from "@/components/site/Consulting";
import About from "@/components/site/About";
import Testimonials from "@/components/site/Testimonials";
import NatureBand from "@/components/site/NatureBand";
import ContactSection from "@/components/site/ContactSection";
import LatestInsights from "@/components/site/LatestInsights";
import Footer from "@/components/site/Footer";
import Seo from "@/components/Seo";

// BookingDialog is only needed after a "Book" click — keep it out of the first-paint bundle.
const BookingDialog = lazy(() => import("@/components/site/BookingDialog"));

export default function Landing() {
  const [content, setContent] = useState(null);
  const [services, setServices] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingMounted, setBookingMounted] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/consultants").then((r) => setConsultants(r.data)).catch(() => {});
  }, []);

  // Direct link: /booking opens the site with the booking dialog already up.
  useEffect(() => {
    if (pathname === "/booking") {
      setBookingMounted(true);
      setBookingOpen(true);
    }
  }, [pathname]);

  if (!content) return <div className="min-h-screen bg-upland-green" />;

  const openBooking = () => { setBookingMounted(true); setBookingOpen(true); };

  return (
    <div className="bg-upland-offwhite">
      <Seo
        title="Management Consulting for Small Business | Upland Group"
        description="Management consulting that pairs high-touch strategy with data-driven, technology-forward execution — built for small and mid-sized businesses ready to scale. Call (844) 727-1001."
        canonical="https://uplandgroupllc.com/"
        ogType="website"
      />
      <Header onBook={openBooking} />
      <Hero content={content} onBook={openBooking} />
      <Services services={services} content={content} onBook={openBooking} />
      <NatureBand content={content} />
      <Consulting services={services} content={content} onBook={openBooking} />
      <ServiceBreakout services={services} section="growth" id="growth" variant="grey"
        bgImage="/api/files/uplandgroup/uploads/189d5803-6a09-47f5-83b2-4170281d4012.webp"
        overline={content.growth_overline} title={content.growth_title} intro={content.growth_intro} />
      <ServiceBreakout services={services} section="management" id="management"
        overline={content.management_overline} title={content.management_title} intro={content.management_intro} onBook={openBooking} />
      <About content={content} consultants={consultants} />
      <Testimonials />
      <LatestInsights />
      <ContactSection content={content} onBook={openBooking} />
      <Footer content={content} />
      {bookingMounted && (
        <Suspense fallback={null}>
          <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} consultants={consultants} />
        </Suspense>
      )}
    </div>
  );
}
